/** @module csi/heading/AggregateController */
define([
	"dojo/_base/declare",
	"underscore",
	"src/c8-platform/service/aggregateStoreService",
	"src/c8-platform/type/uom",
	"src/c8-platform/type/value",
	"csi",
	"csi/action/AggregateMixin",
	"csi/heading/SelectController",
	"csi/locale",
	"csi/middleware/goToDialog/goToDialogService",
	"csi/service/nodeService",
	"csi/store/_base",
	"csi/widget/_ComboBoxMenu",
	"csi/xml",
], function(declare, _, {aggregateStoreResponseHasMultipleKeys}, {isConversionUOMAttributeOnNode}, {attributeValueAsArray}, csi, AggregateMixin, SelectController, locale, {goToDialog}, {resolveNodes}, csiStore, _ComboBoxMenu, xml) {

	return declare([SelectController, AggregateMixin], {
		/** @property {boolean} bOmitGoToDialog - indicate if omits the "Go to Dialog" action */
		bOmitGoToDialog: false,

		/** @property {Names} namesStore - store holding aggregate store results */
		namesStore: null,

		/** @property {boolean} _bPopup - indicates if popup dialog was used */
		_bPopup: false,

		constructor(/*Object*/params) {
			// summary: do not support holding reverse aggregation changes
			if (this.m_action.bReverse) {
				this.bHold = false;
			}
		},

		getFormData() {
			// summary:
			var fd = this.inherited(arguments);

			if (this.heading.m_bRequired) {
				fd.m_bRequireOne = true;
			}

			return fd;
		},

		getOldValue() {
			// summary: make sure oldValue is consistent with AggregateId if different than Heading Id
			const {m_action, heading, _ctx} = this;
			const {aggregateClassIds, aggregateId, bReverse, flattenAggregateClassReferences} = m_action;
			let value = this.inherited(arguments);

			if (!bReverse) {
				if (aggregateId != heading.m_id) {
					value = _ctx.getNodeAttributeValue(aggregateId);
				}

				// convert any aggregate class nodes into their referenced nodes
				if (flattenAggregateClassReferences) {
					const aggregateClassId = aggregateClassIds[0];

					if (typeof value === "string") {
						value = _ctx.cache.get(value)?.[aggregateClassId] || value;
					} else {
						value = _ctx.cache.getNodes(value).map((node) => node[aggregateClassId]);
					}
				}
			}

			return value;
		},

		preProcess() {
			// summary: start with either drop-down or popup dialog; check canBeInlined in case this is an additional AggregateInline action
			// in the tooltip that is DialogOnly
			const {heading, ren, _ctx} = this;

			_ctx.fd = this.getFormData();
			this._getActionViewParameters(_ctx, heading, ren);
			this.delaySearch = heading._rca.isDelayedSearch;

			if (this._canDropDown() && this.m_action.canBeInlined()) {
				this.inherited(arguments);
			} else {
				this._goToDialog();
			}
		},

		/**
		 * @override
		 * Skip call to OnAction since it is called for each aggregated node separately
		 */
		process() {
			this.toWriter();
			this.publish();
		},

		toWriterSelections(/*FormData*/fd, /*CallMethod?*/call, /*String?*/id) {
			// summary: stub out since Aggregate action handles selected nodes differently
		},

		_canDropDown() {
			// summary: indicates if should use drop-down or go straight to popup dialog -
			// if not tab-stop, then cannot be edited inline
			return this.ren.isTabStop(this._ctx, this.heading);
		},

		_editAgain() {
			// summary: if already in popup dialog, then do not restart cell editing
			return this._bPopup ? false : this.inherited(arguments);
		},

		/**
		 * Start process to open dialog
		 * @param {boolean} [changed] - indicates if selections have already been changed and dialog should enable Save button
		 */
		async _goToDialog(changed = false) {
			const {
				m_action,
				heading,
				namesStore,
				oldValue,
				ren,
				_actionViewParameters,
				_ctx,
			} = this;

			const vr = m_action.m_viewRef || _actionViewParameters?.m_viewRef;
			const av = this.getActionView(vr);
			const defView = av?.m_defView;
			const nodeUrl = av?.m_node.$URL;

			// reset members to initial state before popping up dialog
			this._bPopup = true;
			this._bProcessing = false;
			this._keyCode = null;
			this.newValue = null;

			if (defView?.m_typeView === "Wizard") {
				// use Dojo dialog for wizard
				this.openDialog(av);
				return;
			}

			// open dialog with existing view, or if none, then construct view(s) automatically from aggregate store;
			// the names store may not yet exist if DialogOnly is used
			let store = namesStore || ren.getStore(_ctx, heading, oldValue);
			// delayed search case
			if (store.bDelayedRequest) {
				store = store.sourceStore;
			}

			try {
				goToDialog(
					this,
					heading._rca,
					changed,
					store,
					m_action.dialogCardId,
					defView,
					nodeUrl,
					this.onAggregateStoreTableView.bind(this),
				);
			} catch (err) {
				this._onPublishError(err);
			}
		},

		/**
		 * Update old value and form data to reflect current values
		 * @param {IAggregateStoreResponse} res 
		 */
		_onAggregateStoreResponse(res) {
			const {editNode, m_cache, m_formData} = this;

			// FormData works with nodes, so create client ones if not already cached
			// FIX: eventually we should only need URLs, but this is due to current implementation for FormData
			const nodes = res.currentValues?.map((result) => m_cache.get(result.$id) || m_cache.createClientNode(result.$id, null, result[xml.ATTR_NAME])) || [];
			const value = nodes.map(xml.nodeGetURL);

			this.oldValue = this._convertArrayToAggregateValue(value);

			// update form data
			m_formData.resetAggregations();
			nodes.forEach((node) => m_formData.addAggregatedNode(node));

			// update value on widget
			if (editNode) {
				// if slim card used, then update widget to render it rather than simple option
				if (aggregateStoreResponseHasMultipleKeys(res)) {
					editNode.set("renderSlimCards", true);
				}

				if (editNode.resetSelected) {
					editNode.resetSelected(value);
				} else {
					const currentValue = value[0] || csi.EMPTY_URL;
					if (currentValue !== editNode.get("value")) {
						editNode.set("value", currentValue, false);
					}
				}
			}
		},

		/**
		 * Open Go to Dialog with given value representing the existing selections
		 * @param {string | string[]} value 
		 */
		async _processGoToDialog(value) {
			// value must be array bu may be undefined or not array. Example: single select (ref, enum)
			value = attributeValueAsArray(value);

			this._removeDialog();

			// ensure all selections are in the cache
			await resolveNodes(value, this.m_cache);

			// go to dialog - use timeout so that select component is closed before dialog opens
			setTimeout(() => {
				const changed = this._updateFormDataWithSelections(value);
				this._goToDialog(changed);
			}, 1);
		},

		/**
		 * @override
		 * Process Go to Dialog if selected ()
		 */
		_processValue() {
			const {editNode, newValue, _bPopup} = this;

			if (!_bPopup && (newValue == csi.DIALOG_URL)) {
				// only valid for Dojo case - React case will go directly to _processGoToDialog
				this._processGoToDialog(editNode.get("preGoToDialogValue"));
			} else {
				this.inherited(arguments);
			}
		},

		/**
		 * @override
		 * If a given Attribute is using aggregate stores, then check arguments and aggregate store response instead of this.m_action.m_request
		 */
		_shouldResetDependencies() {
			return this.inherited(arguments) && !this.namesStore?.aggregateStoreResponse;
		},

		/**
		 * Add Go to Dialog option to store, and update value if AggregateClass specified so that it references
		 * the results of the store rather than nodes of type AggregateClass
		 */
		_toHtmlDynamic(ctx, params) {
			var action = this.m_action;

			if (action.aggregateClass && action.aggregateClassIds) {
				var value = this.getFormData().getAggregatedReferences();
				if (value.length) {
					this.oldValue = params.value = _.isArray(this.oldValue) ? value : value[0];
				}
			}

			const w = this.inherited(arguments);
			const a = this._actionViewParameters;
			const {store} = w;
			const {aggregateStoreParams} = store;
			let onPlaceholderGoToDialog;

			this.namesStore = store;

			// if aggregate store, then query store in order to get current values (oldValue) rather than rely on client behavior;
			// this is necessary for stores with an aggregate class to work properly
			if (aggregateStoreParams) {
				store.onAggregateStoreResponse = (res) => this._onAggregateStoreResponse(res);
			}

			if (!this.bOmitGoToDialog && (a?.m_viewRef?.getReferencedNode(ctx.cache, ctx.getNode()) || aggregateStoreParams)) {
				store.actionData = [csiStore.nameObject(locale.resource("GoToDialog"), csi.DIALOG_URL)];
				onPlaceholderGoToDialog = () => this._processNewValue(csi.DIALOG_URL);
			}

			if (this.delaySearch) {
				w.enableDelayedSearch(store, onPlaceholderGoToDialog);

				// cannot cache store in this case since it will only have partial results
				this.widget.refreshHeadingStore(this.heading);
			}

			return w;
		},

		/**
		 * @override
		 * if popup not used, then populate form data with selected node from drop-down before continuing with aggregate processing
		 */
		_toWriterAttribute(id, type) {
			const {heading, m_action, namesStore, newValue, oldValue, widget, _bPopup} = this;
			const fd = this.getFormData();
			const node = this.getNode();
			let ctx;

			// refresh node if attribute changed is a UOM conversion attribute with a corresponding UOM value attribute
			if (isConversionUOMAttributeOnNode(node, heading.m_id)) {
				this.bRefreshNode = true;
			}

			if (!_bPopup) {
				// make sure to use drop-down store's cache - otherwise selected node may not be found
				if (_.isString(newValue)) {
					const bChecked = xml.isValidURL(newValue);
					const url = bChecked ? newValue : oldValue;
					ctx = widget.getContext(url);
					fd.changeSelection(ctx, bChecked);
				} else {
					this._updateFormDataWithSelections(newValue);
				}
			}

			// are we dealing with an aggregate store response?
			const store = namesStore?.bDelayedRequest ? namesStore.sourceStore : namesStore;
			if (store?.aggregateStoreResponse) {
				if (m_action.m_request) {
					console.error(`Request found in XML for this operation. Since this operation involves an aggregate store (${node.$Type}.${id}), the request must be moved to Java.`);
					throw new Error(locale.resource("ErrorAction", [m_action.m_id]));
				}

				if (this._processAggregateStoreSelections(fd, id, store)) {
					this._notifyOfChange(null, node, id);
				}
			} else if (this._processFormData(fd)) {
				// notify that attribute value was changed
				this._notifyOfChange(null, node, id);
			}
		},

		/**
		 * Update FormData with selections in multi-select widget
		 * @param {string[]} newValue 
		 * @return {boolean}
		 */
		_updateFormDataWithSelections(newValue) {
			const {oldValue, widget} = this;
			const ctx = widget.getContext(csi.EMPTY_URL);
			const fd = this.getFormData();
			const newArray = newValue ? [].concat(newValue) : null;
			const oldArray = oldValue ? [].concat(oldValue) : null;
			const newSet = new Set(newArray);
			const oldSet = new Set(oldArray);
			let changed = false;

			oldArray?.forEach((url) => {
				if (!newSet.has(url)) {
					fd.changeSelection(ctx.setNodeURL(url), false);
					changed = true;
				}
			});

			newArray?.forEach((url) => {
				if (!oldSet.has(url)) {
					fd.changeSelection(ctx.setNodeURL(url), true);
					changed = true;
				}
			});

			return changed;
		},

		/**
		 * @override
		 * No need to validate Go to Dialog value
		 */
		async validate() {
			// convert to array whether newValue is string or array
			const a = [].concat(this.newValue);
			if (a[0] !== csi.DIALOG_URL) {
				// for some reason this.inherited does not work, so using below technique instead;
				// maybe it has something to do with overriding an async function?
				return SelectController.prototype.validate.call(this);
			}
		},

		/**
		 * @override
		 * Extract new value from dialog
		 */
		async validateAndProcess() {
			if (this._bPopup) {
				const value = this.m_formData.getSelectedValue();
				this.newValue = this._convertArrayToAggregateValue(value);
			}

			return SelectController.prototype.validateAndProcess.call(this);
		},

		/**
		 * Make sure selected nodes still exist - could have been cached and then deleted
		 */
		async onValidate() {
			const {m_cache, heading, newValue, refNode, widget} = this;
			let a;

			// nothing to do if type is not ref (such as for Type="string" and Format="Selection")
			if (!xml.attributeTypeIsRef(heading.getAttrType())) {
				return;
			}

			if (_.isArray(newValue)) {
				a = newValue;
			} else if (_.isString(newValue) && (newValue != csi.EMPTY_URL)) {
				a = [newValue];
			}

			if (a?.length) {
				const paths = widget.getRefNodePaths(refNode, heading.getQueryPath());

				try {
					await m_cache.getNodeDependencies(a, paths, true);
				} catch (err) {
					// reset store so next time will fetch from server
					widget.refreshHeadingStore(heading);
					throw new Error(locale.resource("ValidateNode"));
				}
			}
		},

		/**
		 * Given an array, return an array or single value based on attribute being edited
		 * @param {cnl[]} value 
		 * @return {cnl | cnl[]}
		 */
		_convertArrayToAggregateValue(value) {
			return this.isAggregateSingleSelection() ? (value[0] || csi.EMPTY_URL) : value;
		},

		/** React */

		/**
		 * @override
		 * create list of select actions for React Selects
		 * @param {*} a - action to use for popup view 
		 * @param {Names} store - existing renderer store
		 * @return {SelectAction} list of select actions for React Selects 
		 */
		getSelectActions(a, store) {
			const ctx = this.getContext();
			const effectiveStore = store.bDelayedRequest ? store.sourceStore : store;

			// don't show Go To dialog if store/view are empty, except for delayed search, where store might be empty but Go To dialog should be shown
			if (!this.bOmitGoToDialog &&
				(this.delaySearch || a?.m_viewRef != null || !effectiveStore.isStoreEmpty()) &&
				(a?.m_viewRef?.getReferencedNode(ctx.cache, ctx.getNode()) || effectiveStore?.aggregateStoreParams)
			) {
				return [{
					onAction: (value) => this._processGoToDialog(value || this.oldValue),
					res: "GoToDialog"
				}];
			}

			return [];
		},

		/**
		 * Retrieve values which are already in memory to create placeholder in inline control
		 * @returns {SelectOptions}
		 */
		getReactPlaceholderOptions() {
			const {delaySearch, heading, ren} = this;
			const selectOptions = ren.getReactPlaceholderOptions(this, this.getContext(), heading);
			selectOptions.delaySearch = delaySearch;
			const {store} = selectOptions;
			this.namesStore = store;
			// if aggregate store, then query store in order to get current values (oldValue) rather than rely on client behavior;
			// this is necessary for stores with an aggregate class to work properly
			if (store.aggregateStoreParams) {
				store.onAggregateStoreResponse = (res) => this._onAggregateStoreResponse(res);
			}

			return selectOptions;
		},

	});
});